import React, { useEffect, useState } from 'react'
import { View, ScrollView, FlatList } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import { List } from 'react-native-paper'
import MSFESHeading from '../MSFESHeading'
import MSFESButton from '../MSFESButton'
import { PermissionsRowHeaders } from './PermissionsRowHeaders'
import { PermissionsBlock } from './PermissionsBlock'
import spacing from '../../styles/spacing'
import _ from 'lodash'
import {
  fetchPermissionsDocumentForEditing,
  fetchSystemPermissions,
  savePermissionsDocument
} from '../../store/permission-v2/actions'
import GlobalLoading from '../GlobalLoading'
import { showMessage } from 'react-native-flash-message'
import { useTenantOrGlobalAdmin } from '../../hooks/useTenantOrGlobalAdmin'

export const getKeyForGroup = (group, tenant, role, personnelId) => {
  const key = `${
    tenant && tenant !== 'global' ? `tenant:${tenant}:` : 'global::'
  }role:${role ?? ''}:personnel:${personnelId ?? ''}:module:${
    group?.id ?? '-1'
  }:`

  return key
}

export const getKeyForSystemPermission = (
  systemPermissionName,
  tenant,
  role,
  personnelId
) => {
  const key = `${
    tenant && tenant !== 'global' ? `tenant:${tenant}:` : 'global::'
  }role:${role ?? ''}:personnel:${
    personnelId ?? ''
  }:systemPermission:${systemPermissionName}:`

  return key
}
export const getKeyForEntityType = (entityType, tenant, role, personnelId) => {
  const key = `${
    tenant && tenant !== 'global' ? `tenant:${tenant}:` : 'global::'
  }role:${role ?? ''}:personnel:${personnelId ?? ''}:entityType:${
    entityType?.id
  }:`

  return key
}

export const PermissionsEditor = ({
  tenant,
  role,
  personnelId,
  personnelName,
  navigation,
  setPage
}) => {
  const dispatch = useDispatch()
  const entityTypes = useSelector(
    (AppState) => AppState.entityTypes.dataLite?.data
  )
  const [permissionsDocument, setPermissionsDocument] = useState(null)
  const [loading, setLoading] = useState(false)
  const [systemPermissions, setSystemPermissions] = useState(null)
  const { tenant: tokenTenant } = useTenantOrGlobalAdmin()
  useEffect(() => {
    setLoading(true)
    dispatch(fetchSystemPermissions()).then((systemPermissions) => {
      setSystemPermissions(systemPermissions?.entries)
    })
    dispatch(fetchPermissionsDocumentForEditing({ tenant, role, personnelId }))
      .then((newPermissionsDocument) => {
        setPermissionsDocument(newPermissionsDocument?.entries)
        setLoading(false)
      })
      .catch((e) => {
        setLoading(false)
        console.warn(e)
      })
  }, [tenant, role, personnelId])

  const recordChanges = (changes, group, entityType, systemPermission) => {
    if (group) {
      const key = getKeyForGroup(group, tenant, role, personnelId)
      permissionsDocument[key] = changes
      setPermissionsDocument({
        ...permissionsDocument
      })
    } else if (entityType) {
      const key = getKeyForEntityType(entityType, tenant, role, personnelId)

      permissionsDocument[key] = changes
      setPermissionsDocument({
        ...permissionsDocument
      })
    } else if (systemPermission) {
      const key = getKeyForSystemPermission(
        systemPermission,
        tenant,
        role,
        personnelId
      )

      permissionsDocument[key] = changes
      setPermissionsDocument({
        ...permissionsDocument
      })
    }
  }

  const onSavePressed = () => {
    setLoading(true)
    dispatch(
      savePermissionsDocument({
        permissions: permissionsDocument,
        tenant,
        role,
        personnel: personnelId
      })
    )
      .then((newPermissionsResponse) => {
        setPermissionsDocument(newPermissionsResponse?.data?.permissions)

        showMessage({
          message: 'Permissions saved',
          type: 'success'
        })
        setLoading(false)
      })
      .catch((error) => {
        console.warn(error)
        showMessage({
          message: 'Error saving permissions',
          type: 'danger'
        })
        setLoading(false)
      })
  }

  const [expanded, setExpanded] = useState(null)

  const entityTypesGrouped = Object.entries(
    _.groupBy(
      entityTypes,
      (entityType) =>
        entityType.object_data?.visible_on_calendar?.[0]?.display_name
    )
  ).map(([, members]) => ({
    groupName:
      members[0].object_data?.visible_on_calendar?.[0]?.display_name ??
      '(No Parent)',
    group: members[0].object_data?.visible_on_calendar?.[0] ?? {
      id: -1,
      display_name: '(No Parent)'
    },
    members
  }))

  return (
    <View style={{ flex: 1 }}>
      {loading && <GlobalLoading />}
      <MSFESHeading style={{ paddingHorizontal: spacing.m2 }}>
        Editing permissions for:{' '}
        {tenant && tenant === tokenTenant?.id
          ? tokenTenant?.name
          : tenant ?? 'GLOBAL'}{' '}
        {role ?? ''} {personnelName ?? ''}
      </MSFESHeading>
      <View style={{ flexDirection: 'row' }}>
        <MSFESButton
          title={'Back'}
          variant={'outline'}
          onPress={() => {
            navigation.setParams({
              tenant: undefined,
              role: undefined,
              personnelId: undefined
            })
            setPage(1)
          }}
          style={{ marginLeft: spacing.m2 }}
        />
        <MSFESButton
          title={'Save'}
          onPress={onSavePressed}
          style={{ marginLeft: spacing.m2 }}
        />
      </View>
      <PermissionsRowHeaders />
      <ScrollView style={{ flex: 1, marginHorizontal: spacing.m2 }}>
        <List.AccordionGroup>
          {!loading && (
            <View>
              <MSFESHeading
                iconName={expanded === -1 ? 'chevron-up' : 'chevron-down'}
                onPress={() => {
                  setExpanded(expanded === -1 ? null : -1)
                }}
              >
                Special Permissions
              </MSFESHeading>
              <View>
                {expanded === -1 && (
                  <FlatList
                    data={systemPermissions}
                    keyExtractor={(item, index) => index.toString()}
                    renderItem={({ item }) => (
                      <View>
                        <PermissionsBlock
                          systemPermissionName={item}
                          defaultValue={
                            permissionsDocument?.[
                              getKeyForSystemPermission(
                                item?.name,
                                tenant,
                                role,
                                personnelId
                              )
                            ]
                          }
                          onChanged={(changes) =>
                            recordChanges(changes, null, null, item?.name)
                          }
                        />
                      </View>
                    )}
                  />
                )}
              </View>
            </View>
          )}
          {!loading &&
            entityTypesGrouped.map(({ groupName, group, members }, index) => {
              return (
                <View key={index}>
                  <MSFESHeading
                    iconName={
                      expanded === index ? 'chevron-up' : 'chevron-down'
                    }
                    onPress={() => {
                      setExpanded(expanded === index ? null : index)
                    }}
                  >
                    {groupName}
                  </MSFESHeading>
                  {group?.id && (
                    <PermissionsBlock
                      group={group}
                      defaultValue={
                        permissionsDocument?.[
                          getKeyForGroup(group, tenant, role, personnelId)
                        ]
                      }
                      onChanged={(changes) => recordChanges(changes, group)}
                    />
                  )}
                  <View>
                    {expanded === index && (
                      <FlatList
                        data={members}
                        keyExtractor={(item, index) => index.toString()}
                        renderItem={({ item }) => (
                          <View>
                            <PermissionsBlock
                              entityType={item}
                              defaultValue={
                                permissionsDocument?.[
                                  getKeyForEntityType(
                                    item,
                                    tenant,
                                    role,
                                    personnelId
                                  )
                                ]
                              }
                              onChanged={(changes) =>
                                recordChanges(changes, null, item)
                              }
                            />
                          </View>
                        )}
                      />
                    )}
                  </View>
                </View>
              )
            })}
        </List.AccordionGroup>
      </ScrollView>
    </View>
  )
}
